import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
    Briefcase, CheckSquare, Heart, Home, Check
} from 'lucide-react';
import { useTranslation } from '../../../providers/TranslationProvider';

const Step4Plans = ({ data, onUpdate, isDarkMode }) => {
    const { t } = useTranslation();
    const selectedPlans = data.weeklyPlans || [];

    const planOptions = [
        { id: 'work_school', label: t('onboarding.plans.work'), icon: Briefcase, color: isDarkMode ? 'text-orange-400' : 'text-orange-500' },
        { id: 'todos', label: t('onboarding.plans.todos'), icon: CheckSquare, color: isDarkMode ? 'text-green-400' : 'text-green-500' },
        { id: 'social', label: t('onboarding.plans.social'), icon: Heart, color: isDarkMode ? 'text-purple-400' : 'text-purple-500' },
        { id: 'home', label: t('onboarding.plans.home'), icon: Home, color: isDarkMode ? 'text-yellow-400' : 'text-yellow-500' },
    ];

    const togglePlan = (id) => {
        if (selectedPlans.includes(id)) {
            onUpdate({ weeklyPlans: selectedPlans.filter(item => item !== id) });
        } else {
            onUpdate({ weeklyPlans: [...selectedPlans, id] });
        }
    };

    return (
        <div className="w-full space-y-4">
            <motion.div
                className="grid grid-cols-2 gap-2.5"
                initial="hidden"
                animate="visible"
                variants={{
                    visible: { transition: { staggerChildren: 0.03 } }
                }}
            >
                {planOptions.map((option) => {
                    const isSelected = selectedPlans.includes(option.id);
                    const Icon = option.icon;
                    return (
                        <motion.button
                            key={option.id}
                            variants={{
                                hidden: { opacity: 0, scale: 0.95 },
                                visible: { opacity: 1, scale: 1 }
                            }}
                            whileTap={{ scale: 0.96 }}
                            onClick={() => togglePlan(option.id)}
                            className={`w-full p-4 rounded-xl flex flex-col items-center justify-center gap-2 transition-all duration-200 backdrop-blur-md min-h-[100px] ${isSelected
                                ? 'border-2 border-green-500 bg-green-500/10'
                                : isDarkMode
                                    ? 'border border-white/5 bg-white/5 hover:border-white/20'
                                    : 'border border-slate-200 bg-white/50 hover:bg-slate-50'
                                }`}
                        >
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors duration-200 ${isSelected
                                ? 'bg-green-500 scale-110'
                                : isDarkMode ? 'bg-white/10' : 'bg-slate-100'
                                }`}>
                                {isSelected ? <Check size={16} className="text-white" /> : <Icon size={18} className={option.color} />}
                            </div>

                            <span className={`text-sm font-medium transition-colors duration-200 ${isSelected
                                ? isDarkMode ? 'text-green-400' : 'text-green-700'
                                : isDarkMode ? 'text-white' : 'text-slate-800'
                                }`}>
                                {option.label}
                            </span>
                        </motion.button>
                    );
                })}
            </motion.div>

            {/* Input Area */}
            <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className={`p-3.5 rounded-xl backdrop-blur-md border transition-all duration-200 ${isDarkMode
                    ? 'bg-white/5 border-white/5 focus-within:border-white/20'
                    : 'bg-white/50 border-slate-200 focus-within:border-slate-300'
                    }`}
            >
                <input
                    type="text"
                    value={data.customPlan || ''}
                    onChange={(e) => onUpdate({ customPlan: e.target.value })}
                    placeholder={t('onboarding.plans.placeholder')}
                    className={`w-full bg-transparent outline-none text-base placeholder-opacity-50 ${isDarkMode ? 'text-white placeholder-slate-400' : 'text-slate-900 placeholder-slate-400'}`}
                />
            </motion.div>
        </div>
    );
};

export default Step4Plans;
